import { AuthService } from "./auth.service";
import { dispatch } from "../utils/Extension";
import ApiService from "./api.service";
import { toast, Id as ToastId } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import logger from "../utils/logger";
import { isRecording } from "../utils/sync";

// Warning will be shown 1 minute before logout
const WARNING_BEFORE_LOGOUT = 60000; // 1 minute in milliseconds
// Check for inactivity every 15 seconds (reduced from 30s for better responsiveness)
const CHECK_INTERVAL = 15000; // 15 seconds
// Throttle high-frequency events to update at most once per 500ms (reduced from 1000ms)
const THROTTLE_INTERVAL = 500; // 500ms
// Persist lastActive to localStorage so a tab that Chrome discards/freezes overnight
// can be detected as expired on resume. Without this, a resurrected tab reinstantiates
// AutoLogout with lastActive=Date.now() and the stale session silently gets another
// full timeout window.
const LAST_ACTIVE_STORAGE_KEY = 'atpt_autologout_last_active';
// Throttle synchronous storage writes to at most one per PERSIST_MIN_INTERVAL.
// Enough resolution for a 45-min timeout; avoids writing on every keystroke.
const PERSIST_MIN_INTERVAL = 5000; // 5 seconds

export default class AutoLogout {
  private static instance: AutoLogout | null = null;
  private api: ApiService;
  private minutesToTimeout: number;
  private lastActive: number;
  private toastId: ToastId | null;
  private isShowingWarning: boolean;
  private checkIntervalId: NodeJS.Timeout | null;
  private activityEvents: string[];
  private boundUpdateLastActive: () => void;
  private boundHandleVisibilityChange: () => void;
  private boundHandleStorageEvent: ((e: StorageEvent) => void) | null;
  private boundHandleMessageEvent: ((e: MessageEvent) => void) | null;
  private throttledUpdate: (() => void) | null;
  private throttleTimer: NodeJS.Timeout | null;
  private isMonitoring: boolean;
  private tokenMissingTimestamp: number | null;
  private iframeFocusIntervalId: NodeJS.Timeout | null;
  private lastIframeFocusTime: number;
  // Tracks the last value we wrote to localStorage so we can throttle writes.
  // 0 means "nothing persisted yet" — first setLastActive call will write.
  private lastPersistedActive: number;

  constructor(minutesToTimeout: number = 10) {
    // Singleton pattern - prevent multiple instances
    if (AutoLogout.instance) {
      logger.warn("AUTO_LOGOUT", "AutoLogout instance already exists, returning existing instance");
      return AutoLogout.instance;
    }

    this.api = ApiService.getInstance();
    this.minutesToTimeout = minutesToTimeout;
    this.lastActive = Date.now();
    this.toastId = null;
    this.isShowingWarning = false;
    this.checkIntervalId = null;
    this.throttleTimer = null;
    this.throttledUpdate = null;
    this.isMonitoring = false;
    this.tokenMissingTimestamp = null;
    this.iframeFocusIntervalId = null;
    this.boundHandleStorageEvent = null;
    this.boundHandleMessageEvent = null;
    this.lastIframeFocusTime = 0;
    this.lastPersistedActive = 0;

    // Bind the method once and store the reference
    this.boundUpdateLastActive = this.updateLastActive.bind(this);
    this.boundHandleVisibilityChange = this.handleVisibilityChange.bind(this);
    // 💓 Recording-heartbeat from extension's frame-parent bridge (only fires on filtered
    // window.postMessage with __atpt_recording_heartbeat__ tag — won't catch generic noise).
    this.boundHandleMessageEvent = this.handleRecordingHeartbeat.bind(this);

    // Comprehensive list of ALL events that indicate user activity
    // Using capture phase (true) to catch events even when stopPropagation() is called
    // This ensures we detect:
    // - Image clicks (click events bubble up)
    // - Drag operations (dragstart, drag, dragover, dragend, drop)
    // - Menu interactions, selections, pointer events, etc.
    // Only events that REQUIRE physical user interaction.
    // Removed: focus/blur/focusin/focusout (fire programmatically on route changes),
    //          mouseenter/mouseleave/mouseover/mouseout (fire on CSS transitions),
    //          selectionchange/selectstart (fire on programmatic DOM updates),
    //          SVGLoad/SVGUnload (fire on component render),
    //          framecommand (browser extension noise),
    //          message/postMessage (extension/webpack/devtools noise)
    this.activityEvents = [
      // Mouse — only intentional actions
      "mousedown",
      "click",
      "dblclick",
      "contextmenu",

      // Keyboard
      "keydown",
      "keyup",

      // Form input — user typing/selecting
      "input",
      "change",
      "submit",

      // Touch (mobile/tablet)
      "touchstart",
      "touchend",

      // Pointer (unified mouse/touch/pen)
      "pointerdown",
      "pointerup",

      // Drag and Drop — user-initiated
      "dragstart",
      "dragend",
      "drop",

      // Scroll and wheel — user scrolling
      "scroll",
      "wheel",

      // Clipboard — user action
      "cut",
      "copy",
      "paste",
    ];

    // Don't start monitoring here - let InactivityProvider handle it
    // This allows better control over when monitoring starts/stops
    logger.info("AUTO_LOGOUT", "Auto-logout service created", {
      timeoutMinutes: minutesToTimeout,
      checkInterval: CHECK_INTERVAL / 1000 + "s",
      warningBefore: WARNING_BEFORE_LOGOUT / 1000 + "s",
      throttleInterval: THROTTLE_INTERVAL + "ms",
      hasToken: !!AuthService.token,
      willStartMonitoring: "Managed by InactivityProvider"
    });

    AutoLogout.instance = this;
  }

  /**
   * Get the singleton instance
   */
  public static getInstance(): AutoLogout | null {
    return AutoLogout.instance;
  }

  /**
   * Start monitoring - call this after user logs in
   */
  public start(): void {
    if (!AuthService.token) {
      logger.warn("AUTO_LOGOUT", "Cannot start monitoring - user not authenticated");
      console.warn("🚨 AUTO_LOGOUT: Cannot start - no token found");
      return;
    }

    if (this.isMonitoring) {
      logger.debug("AUTO_LOGOUT", "Monitoring already active, skipping start");
      console.log("✅ AUTO_LOGOUT: Already monitoring");
      return;
    }

    console.log("🚀 AUTO_LOGOUT: Starting monitoring", {
      timeoutMinutes: this.minutesToTimeout,
      checkInterval: CHECK_INTERVAL / 1000 + "s",
      hasToken: !!AuthService.token
    });

    // Restore lastActive from a prior session if the tab was discarded/frozen and
    // reloaded. Without this, a resurrected tab would silently reset the counter
    // and grant another full timeout window.
    //
    // BUT — the naive "always trust the stored value" approach breaks on a fresh
    // login after the browser was closed without a Logout click: destroy() never
    // ran, the stored value lingered from the prior session (potentially days
    // old), and start() saw it and immediately logged the new session out. Bug
    // report 2026-07-18: "logged in after days, got kicked out with
    // 'Inactivity timeout (349433s idle)'".
    //
    // Discriminator: compare the current token's iat (issued-at) against the
    // stored lastActive. Activity can only happen AFTER a token is issued, so:
    //   - stored >= token.iat → activity is within THIS token's lifetime → the
    //     storage belongs to this session → trust it (resurrected tab).
    //   - stored <  token.iat → the storage predates this token → it's from a
    //     PRIOR session → discard it and start fresh (fresh login case).
    // Fail-safe: any decode error or missing iat → treat as fresh (don't punish
    // users for tokens we can't parse).
    const stored = this.readStoredLastActive();
    const tokenIatMs = this.getTokenIssuedAtMs();
    const storedBelongsToPriorSession = stored !== null && tokenIatMs !== null && stored < tokenIatMs;

    if (stored !== null && !storedBelongsToPriorSession) {
      this.lastActive = stored;
      this.lastPersistedActive = stored;
      logger.info("AUTO_LOGOUT", "Restored lastActive from storage (same-session resurrection)", {
        storedAgoSeconds: Math.round((Date.now() - stored) / 1000),
        timeoutMinutes: this.minutesToTimeout
      });
    } else {
      if (storedBelongsToPriorSession) {
        logger.info("AUTO_LOGOUT", "Discarding stored lastActive from prior session", {
          storedAgoSeconds: Math.round((Date.now() - (stored as number)) / 1000),
          storedIso: new Date(stored as number).toISOString(),
          tokenIatIso: new Date(tokenIatMs as number).toISOString()
        });
      }
      // Force an immediate persist by resetting the throttle tracker — otherwise
      // the very next tab that starts up could still read the stale value
      // during the 5-second write window.
      this.lastPersistedActive = 0;
      this.setLastActive(Date.now());
    }

    this.startMonitoring();

    // Immediate check — if the restored timestamp is already past the timeout
    // (tab discarded overnight), log out before we ever render stale UI. Safe
    // for the fresh-session branch too since lastActive was just set to now.
    this.checkTimeout();

    console.log("✅ AUTO_LOGOUT: Monitoring started successfully", {
      isMonitoring: this.isMonitoring,
      checkIntervalId: !!this.checkIntervalId
    });
  }

  /**
   * Stop monitoring - call this when user logs out
   */
  public stop(): void {
    if (!this.isMonitoring) {
      return;
    }
    this.destroy();
  }

  private startMonitoring(): void {
    if (this.isMonitoring) {
      logger.warn("AUTO_LOGOUT", "Monitoring already started, skipping");
      return;
    }

    // Set up interval to check for inactivity
    this.checkIntervalId = setInterval(this.checkTimeout.bind(this), CHECK_INTERVAL);

    // Create throttled update function for high-frequency events
    this.throttledUpdate = () => {
      if (!this.throttleTimer) {
        this.throttleTimer = setTimeout(() => {
          this.updateLastActive();
          this.throttleTimer = null;
        }, THROTTLE_INTERVAL);
      }
    };

    // Attach event listeners for user activity
    // Use CAPTURE phase (true) to catch events even when stopPropagation() is used
    // This ensures we detect ALL interactions including:
    // - Image clicks (click events bubble up to document)
    // - Drag operations (dragstart, drag, dragover, dragend, drop)
    // - Menu interactions, selections, pointer events, etc.
    const highFrequencyEvents = [
      "scroll",
      "wheel",
    ];
    
    this.activityEvents.forEach(event => {
      if (highFrequencyEvents.includes(event)) {
        // High-frequency events use throttled handler to avoid performance issues
        document.addEventListener(event, this.throttledUpdate!, { capture: true, passive: true });
      } else {
        // Other events update immediately (clicks, dragstart, dragend, drop, etc.)
        document.addEventListener(event, this.boundUpdateLastActive, { capture: true, passive: true });
      }
    });

    // Visibility change — used ONLY for logout check, NOT for resetting timer.
    // When tab returns from hidden state, checkTimeout runs and logs out if exceeded.
    document.addEventListener('visibilitychange', this.boundHandleVisibilityChange);

    // 💓 Recording-heartbeat listener — extension's frame-parent.js bridge posts a window
    // message tagged __atpt_recording_heartbeat__ whenever a recording event is captured
    // in any tab. This lets us count cross-tab recording activity as user activity, so a
    // long Greyhound recording session doesn't trigger an inactivity logout. Filter is
    // strict (must have our exact flag) so extension/framework postMessage noise is ignored.
    if (this.boundHandleMessageEvent) {
      window.addEventListener('message', this.boundHandleMessageEvent);
    }

    // REMOVED: window 'focus' — token refresh and browser extensions trigger focus events
    // REMOVED: window 'pageshow' — not reliable user activity indicator
    // REMOVED: window 'storage' — token refresh writes trigger cross-tab storage events
    // REMOVED: window 'message' — extensions and frameworks fire postMessage constantly
    //
    // The ONLY sources of lastActive reset are now:
    // 1. The 19 DOM events on document (physical user input)
    // 2. recordApiActivity() for non-background API responses
    // 3. Iframe first-focus detection (polling below)

    // Poll for iframe focus — when a cross-origin iframe has focus, the parent
    // can't receive mouse/keyboard events from inside it.
    // IMPORTANT: Only count as activity when iframe FIRST gains focus (user clicked into it).
    // Continuous polling of the same iframe focus is NOT user activity — the user may
    // have clicked into an iframe and then walked away.
    this.iframeFocusIntervalId = setInterval(() => {
      const activeEl = document.activeElement;
      if (activeEl && activeEl.tagName === 'IFRAME') {
        const now = Date.now();
        // Only reset if iframe was NOT focused in the previous poll (new interaction)
        if (now - this.lastIframeFocusTime > 5000) {
          this.updateLastActive();
        }
        this.lastIframeFocusTime = now;
      } else {
        this.lastIframeFocusTime = 0;
      }
    }, 2000);

    this.isMonitoring = true;

    console.log("✅ AUTO_LOGOUT: Activity monitoring started", {
      eventsCount: this.activityEvents.length,
      usingCapturePhase: true,
      tabVisibilityDetection: true,
      checkIntervalId: !!this.checkIntervalId,
      isMonitoring: this.isMonitoring
    });

    logger.info("AUTO_LOGOUT", "✅ Activity monitoring started", {
      eventsCount: this.activityEvents.length,
      usingCapturePhase: true,
      tabVisibilityDetection: true,
      checkIntervalId: !!this.checkIntervalId,
      isMonitoring: this.isMonitoring
    });
  }

  /**
   * Handle tab visibility change - reset timer when user returns to tab
   */
  private handleVisibilityChange(): void {
    if (!document.hidden) {
      // Tab became visible — check if inactivity timeout was exceeded while hidden
      const inactiveTime = Date.now() - this.lastActive;
      const timeoutMs = this.minutesToTimeout * 60 * 1000;
      if (inactiveTime >= timeoutMs) {
        // Don't logout if recording is active — user is working on external tab
        // Safety cap: 2 hours max protection to handle stale recording flags
        if (isRecording() && inactiveTime < 1 * 60 * 60 * 1000) {
          logger.info("AUTO_LOGOUT", "⏸️ Tab returned after timeout but recording active — resetting timer");
          this.setLastActive(Date.now());
          return;
        }
        logger.error("AUTO_LOGOUT", "Tab returned but inactivity timeout exceeded — logging out", {
          inactiveMinutes: Math.round(inactiveTime / 60000),
          timeoutMinutes: this.minutesToTimeout
        });
        console.error("🚨 AUTO_LOGOUT: Tab returned after timeout — logging out");
        this.logOut();
        return;
      }
      // Not timed out — do NOT reset timer here.
      // Returning to a tab is not physical user input.
      // Only mouse/keyboard/touch events should reset the timer.
    }
  }

  /**
   * 💓 Recording heartbeat from the browser extension.
   *
   * frame-parent.js (extension bridge, runs only on *.atpt.io) posts a window.message
   * tagged __atpt_recording_heartbeat__ whenever the recording side captures an activity
   * on any tab (Greyhound, demoqa, etc.). We treat this as user activity equivalent to a
   * click on the ATPT tab — reset lastActive so the inactivity timer doesn't fire while
   * the user is actively recording.
   *
   * Filter is strict (must have exact flag) so we don't accidentally count extension /
   * framework / devtools postMessage noise as activity (that's why window.message was
   * REMOVED from the activity event list earlier).
   *
   * If for some reason the heartbeat fires when the user is genuinely NOT recording (e.g.
   * extension misbehaves), it's a benign over-extension of the session — not a security
   * regression. The user can still log out manually.
   */
  private handleRecordingHeartbeat(e: MessageEvent): void {
    if (!e.data || typeof e.data !== 'object') return;
    if ((e.data as any).__atpt_recording_heartbeat__ !== true) return;

    // Only update if we're actually monitoring — avoids state mutation post-logout
    if (!this.isMonitoring) return;

    const previousLastActive = this.lastActive;
    this.setLastActive(Date.now());
    // Throttled log — too noisy at INFO if it fires every recording event
    if (this.lastActive - previousLastActive > 30000) {
      logger.info("AUTO_LOGOUT", "💓 Recording heartbeat received — resetting idle timer", {
        inactiveSecondsBeforeReset: Math.round((Date.now() - previousLastActive) / 1000),
        timestamp: (e.data as any).timestamp
      });
    }
  }

  /**
   * Sets lastActive (in memory) AND throttled-persists it to localStorage so the
   * value survives tab discard/freeze. Called by EVERY code path that resets the
   * idle timer — funneling writes through here is what makes the storage layer
   * reliable. Writes are throttled to PERSIST_MIN_INTERVAL to avoid synchronous
   * storage IO on every keystroke.
   */
  private setLastActive(t: number): void {
    this.lastActive = t;
    if (t - this.lastPersistedActive > PERSIST_MIN_INTERVAL) {
      try {
        localStorage.setItem(LAST_ACTIVE_STORAGE_KEY, String(t));
        this.lastPersistedActive = t;
      } catch {
        // localStorage full/blocked (private mode, quota) — non-fatal.
        // In-memory tracking still works; we just lose tab-discard recovery.
      }
    }
  }

  /**
   * Reads the last persisted lastActive value from localStorage. Returns null if
   * nothing is stored, the value is corrupted, or reading throws. Rejects future
   * timestamps (clock skew / storage tampering) with a 60s tolerance.
   */
  private readStoredLastActive(): number | null {
    try {
      const raw = localStorage.getItem(LAST_ACTIVE_STORAGE_KEY);
      if (!raw) return null;
      const n = Number(raw);
      if (!Number.isFinite(n) || n <= 0 || n > Date.now() + 60_000) return null;
      return n;
    } catch {
      return null;
    }
  }

  /**
   * Returns the current JWT's iat (issued-at) claim in ms since epoch, or null
   * if there's no token, the token can't be decoded, or the token has no iat.
   * Used by {@link #start} to tell "fresh login" (token issued after stored
   * lastActive) from "resurrected tab" (token issued before stored lastActive).
   * The jwt-decode lib is already a codebase dep — see AuthService / ApiService
   * for other usages.
   */
  private getTokenIssuedAtMs(): number | null {
    const token = AuthService.token;
    if (!token) return null;
    try {
      const decoded = jwtDecode<{ iat?: number }>(token);
      if (typeof decoded.iat !== "number" || decoded.iat <= 0) return null;
      // iat is in seconds per RFC 7519; lastActive is ms — normalize.
      return decoded.iat * 1000;
    } catch {
      return null;
    }
  }

  private updateLastActive(): void {
    // Double-check authentication before updating
    if (!AuthService.token) {
      // During a token refresh, the access token may be momentarily unavailable.
      // Check refresh token AND give a grace window before destroying monitoring.
      // IMPORTANT: Do NOT reset lastActive here — only real user events should reset it.
      // Resetting during token refresh would keep the session alive indefinitely.
      if (AuthService.refreshToken) {
        // Access token gone but refresh token exists — likely mid-refresh, ignore
        return;
      }

      // Both tokens missing — but don't destroy immediately.
      // The token refresh may be in-flight. Give it a 10-second grace period.
      if (!this.tokenMissingTimestamp) {
        this.tokenMissingTimestamp = Date.now();
        logger.warn("AUTO_LOGOUT", "Token missing — starting grace period", {
          pathname: window.location.pathname,
          timestamp: new Date().toISOString()
        });
        return;
      }
      if (Date.now() - this.tokenMissingTimestamp < 10000) {
        // Within grace period — don't destroy yet
        return;
      }

      logger.warn("AUTO_LOGOUT", "Token missing beyond grace period — stopping monitoring", {
        pathname: window.location.pathname,
        timestamp: new Date().toISOString(),
        gracePeriodMs: Date.now() - this.tokenMissingTimestamp
      });
      console.warn("🚨 AUTO_LOGOUT: Token missing beyond grace period - stopping monitoring");
      this.tokenMissingTimestamp = null;
      this.destroy();
      return;
    }

    // Token is present — clear any grace period tracking
    this.tokenMissingTimestamp = null;

    const previousActive = this.lastActive;
    const inactiveBeforeActivity = Date.now() - previousActive;
    this.setLastActive(Date.now());

    // DEBUG: Log every timer reset with caller info when gap > 30 seconds
    // This helps trace what's keeping the session alive
    if (inactiveBeforeActivity > 30000) {
      const caller = new Error().stack?.split('\n')[2]?.trim() || 'unknown';
      console.warn(`🔍 AUTO_LOGOUT: lastActive RESET after ${Math.round(inactiveBeforeActivity / 1000)}s idle. Caller: ${caller}`);
    }

    // If warning was showing, dismiss it
    if (this.isShowingWarning) {
      logger.info("AUTO_LOGOUT", "✅ User activity detected - dismissing warning", {
        inactiveSecondsBeforeActivity: Math.round(inactiveBeforeActivity / 1000),
        pathname: window.location.pathname
      });
      console.log("✅ AUTO_LOGOUT: User activity detected - dismissing warning", {
        inactiveSeconds: Math.round(inactiveBeforeActivity / 1000),
        pathname: window.location.pathname
      });
      this.isShowingWarning = false;
      if (this.toastId) {
        toast.dismiss(this.toastId);
        this.toastId = null;
      }
    }

    // Log activity detection (throttled to avoid spam) - log if gap > 5 seconds
    if (inactiveBeforeActivity > 5000) {
      const activityInfo = {
        inactiveSecondsBeforeActivity: Math.round(inactiveBeforeActivity / 1000),
        lastActive: new Date(previousActive).toISOString(),
        currentTime: new Date().toISOString(),
        pathname: window.location.pathname,
        documentHasFocus: document.hasFocus()
      };
      
      logger.info("AUTO_LOGOUT", "👆 User activity detected", activityInfo);
      
      // Only log to console if gap was significant (> 30 seconds)
      if (inactiveBeforeActivity > 30000) {
        console.log("✅ AUTO_LOGOUT: Activity detected after inactivity", {
          inactiveSeconds: Math.round(inactiveBeforeActivity / 1000),
          pathname: window.location.pathname
        });
      }
    }
  }

  private checkTimeout(): void {
    if (!this.isMonitoring) {
      console.warn("⚠️ AUTO_LOGOUT: checkTimeout called but monitoring is not active");
      return;
    }

    const inactiveTime = Date.now() - this.lastActive;
    const timeoutMs = this.minutesToTimeout * 60 * 1000;
    const warningThreshold = timeoutMs - WARNING_BEFORE_LOGOUT;
    const inactiveSeconds = Math.round(inactiveTime / 1000);
    const remainingSeconds = Math.round((timeoutMs - inactiveTime) / 1000);

    // DEBUG: Always log every 5 minutes to confirm timer is ticking
    if (inactiveTime % 300000 < CHECK_INTERVAL) {
      console.log(`⏰ AUTO_LOGOUT HEARTBEAT: idle ${Math.round(inactiveTime/60000)}min / ${this.minutesToTimeout}min, hidden=${document.hidden}, focus=${document.hasFocus()}`);
    }

    // Log every 5 minutes normally; every check when close to timeout
    const shouldLogDetails = inactiveTime >= warningThreshold || inactiveTime % 300000 < CHECK_INTERVAL;
    
    const checkInfo = {
      inactiveSeconds,
      remainingSeconds,
      inactiveMinutes: Math.round(inactiveTime / 60000),
      timeoutMinutes: this.minutesToTimeout,
      shouldWarn: inactiveTime >= warningThreshold && !this.isShowingWarning,
      shouldLogout: inactiveTime >= timeoutMs,
      lastActiveTime: new Date(this.lastActive).toLocaleTimeString(),
      lastActiveISO: new Date(this.lastActive).toISOString(),
      currentTime: new Date().toLocaleTimeString(),
      isMonitoring: this.isMonitoring,
      checkIntervalId: !!this.checkIntervalId,
      pathname: window.location.pathname,
      documentHasFocus: document.hasFocus(),
      documentVisibilityState: document.visibilityState,
      hasToken: !!AuthService.token
    };
    
    if (shouldLogDetails) {
      console.log("⏱️ AUTO_LOGOUT: Inactivity check", checkInfo);
      logger.info("AUTO_LOGOUT", "⏱️ Inactivity check", checkInfo);
    }
    
    // Extra warning logs when close to timeout
    if (inactiveTime >= warningThreshold) {
      console.warn("⚠️ AUTO_LOGOUT: Close to timeout", {
        inactiveMinutes: Math.round(inactiveTime / 60000),
        remainingSeconds: remainingSeconds,
        showingWarning: this.isShowingWarning,
        pathname: window.location.pathname,
        documentHasFocus: document.hasFocus(),
        hasToken: !!AuthService.token
      });
    }

    // When tab is hidden, browsers throttle setInterval (fires less often).
    // If timeout is reached while hidden, DON'T logout (user can't see the warning)
    // and DON'T reset the timer. When they return, handleVisibilityChange will
    // check the inactiveTime and logout immediately if exceeded.
    if (inactiveTime >= timeoutMs && document.hidden) {
      logger.debug("AUTO_LOGOUT", "Timeout reached but tab hidden — will logout when tab returns", {
        documentHidden: document.hidden,
        inactiveMinutes: Math.round(inactiveTime / 60000)
      });
      // Do NOT reset lastActive — let the timeout stay exceeded
      // handleVisibilityChange will trigger logout when user returns
      return;
    }

    // ⚠️ FIX: Don't logout during active recording sessions.
    // When the user is recording on an external tab (e.g. demoqa.com), they're
    // actively working but the ATPT app tab doesn't see any DOM events. Without
    // this check, the auto-logout timer expires and logs them out mid-recording,
    // causing activities to be only partially saved.
    // Safety cap: if recording has been "active" for over 2 hours without any
    // DOM activity on the ATPT tab, treat the recording flag as stale (e.g.,
    // browser crashed with recording ON) and proceed with normal logout.
    const MAX_RECORDING_PROTECTION_MS = 1 * 60 * 60 * 1000; // 1 hour
    if (inactiveTime >= timeoutMs && isRecording() && inactiveTime < MAX_RECORDING_PROTECTION_MS) {
      logger.info("AUTO_LOGOUT", "⏸️ Recording is active — resetting inactivity timer", {
        inactiveMinutes: Math.round(inactiveTime / 60000),
        timeoutMinutes: this.minutesToTimeout
      });
      this.setLastActive(Date.now());
      return;
    }

    // Check if user has been inactive for the full timeout period
    if (inactiveTime >= timeoutMs) {
      const logoutInfo = {
        inactiveMinutes: Math.round(inactiveTime / 60000),
        inactiveSeconds: inactiveSeconds,
        lastActive: new Date(this.lastActive).toISOString(),
        currentTime: new Date().toISOString(),
        pathname: window.location.pathname,
        documentHasFocus: document.hasFocus(),
        documentVisibilityState: document.visibilityState,
        warningWasShown: this.isShowingWarning,
        hasToken: !!AuthService.token
      };
      
      logger.error("AUTO_LOGOUT", "❌ Inactivity timeout reached - logging out", logoutInfo);
      console.error("🚨 AUTO_LOGOUT: LOGGING OUT - Inactivity timeout reached", logoutInfo);
      this.logOut();
      return;
    }

    // Show warning 1 minute before logout (only when app is focused — no point
    // showing a toast the user can't see; the defer block above resets the timer
    // when unfocused, so the warning will appear when they return and go idle again)
    if (inactiveTime >= warningThreshold && !this.isShowingWarning && document.hasFocus()) {
      logger.warn("AUTO_LOGOUT", "⚠️ Showing inactivity warning", {
        remainingSeconds: remainingSeconds,
        inactiveMinutes: Math.round(inactiveTime / 60000),
        pathname: window.location.pathname
      });
      console.warn("⚠️ AUTO_LOGOUT: Showing inactivity warning", {
        remainingSeconds: remainingSeconds,
        inactiveMinutes: Math.round(inactiveTime / 60000)
      });
      this.showWarning();
    }
  }

  private showWarning(): void {
    this.isShowingWarning = true;
    const remainingSeconds = Math.ceil(WARNING_BEFORE_LOGOUT / 1000);

    logger.info("AUTO_LOGOUT", "Showing inactivity warning", {
      remainingSeconds
    });

    this.toastId = toast.warn(
      `You will be logged out due to inactivity in ${remainingSeconds} seconds. Please perform any activity to stay logged in.`,
      {
        autoClose: WARNING_BEFORE_LOGOUT,
        closeOnClick: true,
        onClick: () => {
          logger.debug("AUTO_LOGOUT", "Warning toast clicked - resetting timer");
          this.updateLastActive();
        },
        onClose: () => {
          logger.debug("AUTO_LOGOUT", "Warning toast closed");
          this.isShowingWarning = false;
          this.toastId = null;
        }
      }
    );
  }

  private logOut(): void {
    const logoutDetails = {
      timestamp: new Date().toISOString(),
      pathname: window.location.pathname,
      hasToken: !!AuthService.token,
      lastActive: new Date(this.lastActive).toISOString(),
      inactiveDuration: Math.round((Date.now() - this.lastActive) / 1000) + "s",
      warningWasShown: this.isShowingWarning,
      documentHasFocus: document.hasFocus(),
      documentVisibilityState: document.visibilityState
    };
    
    logger.error("AUTO_LOGOUT", "🚨 Performing auto-logout", logoutDetails);
    console.error("🚨 AUTO_LOGOUT: PERFORMING AUTO-LOGOUT", logoutDetails);

    // Persist the logout reason so the login page can display it
    AuthService.setLogoutReason(
      `Inactivity timeout (${logoutDetails.inactiveDuration} idle on ${logoutDetails.pathname})`
    );

    // Clean up intervals
    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
    }
    if (this.iframeFocusIntervalId) {
      clearInterval(this.iframeFocusIntervalId);
      this.iframeFocusIntervalId = null;
    }

    // Clear the persisted lastActive — next login should start with a fresh timer,
    // not immediately re-fire this same logout from stale storage.
    try { localStorage.removeItem(LAST_ACTIVE_STORAGE_KEY); } catch { /* non-fatal */ }
    this.lastPersistedActive = 0;

    // Dismiss ANY and ALL toasts before showing logout message
    toast.dismiss(); // Dismiss all toasts
    this.toastId = null;
    this.isShowingWarning = false;

    // Show message to browser extension
    dispatch({
      type: "show-message-on-client-page",
      message: "Logged out due to inactivity. Please login again on uiflowdesign.",
      loggedOut: true,
      time: 10000
    });

    // Show logout toast AFTER dismissing all previous toasts
    setTimeout(() => {
      toast.info("You have been logged out due to inactivity.", {
        autoClose: 5000
      });
    }, 100);

    // Call logout API and clear auth tokens
    logger.info("AUTO_LOGOUT", "Calling logout API");
    console.log("🔐 AUTO_LOGOUT: Calling logout API");
    
    this.api
      .signout("/api/auth/signout", {})
      .then(() => {
        logger.info("AUTO_LOGOUT", "✅ Logout API call successful - calling globalLogout()", logoutDetails);
        console.log("✅ AUTO_LOGOUT: Logout API successful - calling globalLogout()");
        AuthService.globalLogout();
      })
      .catch(error => {
        logger.error("AUTO_LOGOUT", "❌ Logout API call failed - still calling globalLogout()", { 
          error: error?.message || error,
          ...logoutDetails
        });
        console.error("❌ AUTO_LOGOUT: Logout API failed - still calling globalLogout()", error);
        AuthService.globalLogout();
      });
  }

  /**
   * Destroy the auto-logout service and clean up event listeners
   */
  public destroy(): void {
    if (!this.isMonitoring) {
      return; // Already destroyed
    }

    logger.info("AUTO_LOGOUT", "Destroying auto-logout service");

    this.isMonitoring = false;

    // Clear the persisted lastActive on intentional teardown (manual logout,
    // token grace-period expiry, provider unmount). A subsequent login should
    // start with a fresh timer. Tab discard bypasses destroy() entirely, so
    // storage survives that path — which is what enables the recovery check.
    try { localStorage.removeItem(LAST_ACTIVE_STORAGE_KEY); } catch { /* non-fatal */ }
    this.lastPersistedActive = 0;

    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
    }

    if (this.toastId) {
      toast.dismiss(this.toastId);
      this.toastId = null;
    }

    // Clear throttle timer if active
    if (this.throttleTimer) {
      clearTimeout(this.throttleTimer);
      this.throttleTimer = null;
    }

    // Remove event listeners using the same references and options that were added
    const highFrequencyEvents = [
      "scroll",
      "wheel",
    ];
    
    this.activityEvents.forEach(event => {
      if (highFrequencyEvents.includes(event)) {
        if (this.throttledUpdate) {
          document.removeEventListener(event, this.throttledUpdate, { capture: true } as EventListenerOptions);
        }
      } else {
        document.removeEventListener(event, this.boundUpdateLastActive, { capture: true } as EventListenerOptions);
      }
    });

    // Remove visibility change listener
    document.removeEventListener('visibilitychange', this.boundHandleVisibilityChange);

    // 💓 Remove recording-heartbeat listener
    if (this.boundHandleMessageEvent) {
      window.removeEventListener('message', this.boundHandleMessageEvent);
    }

    // Stop iframe focus polling
    if (this.iframeFocusIntervalId) {
      clearInterval(this.iframeFocusIntervalId);
      this.iframeFocusIntervalId = null;
    }

    logger.debug("AUTO_LOGOUT", "✅ Event listeners cleaned up successfully");
  }

  /**
   * Reset the activity timer (useful for manual reset or testing)
   */
  public resetTimer(): void {
    this.setLastActive(Date.now());
    logger.debug("AUTO_LOGOUT", "Activity timer manually reset");
  }

  /**
   * Record API activity — call this from api.service when responses are received.
   * This prevents logout while the app is actively communicating with the server
   * (e.g., watching test execution, AI generation, polling results).
   *
   * Also dismisses the inactivity warning toast if showing — a successful API
   * response proves the user's session is active (e.g., daemon command execution,
   * batch status polling, WebSocket messages).
   */
  public recordApiActivity(): void {
    if (!this.isMonitoring) return;
    const gap = Date.now() - this.lastActive;
    this.setLastActive(Date.now());
    // DEBUG: Log API activity resets when gap > 30s
    if (gap > 30000) {
      console.warn(`🔍 AUTO_LOGOUT: recordApiActivity RESET after ${Math.round(gap / 1000)}s idle`);
    }

    // Dismiss inactivity warning if showing — API activity proves session is active
    if (this.isShowingWarning) {
      logger.info("AUTO_LOGOUT", "✅ API activity detected - dismissing warning");
      this.isShowingWarning = false;
      if (this.toastId) {
        toast.dismiss(this.toastId);
        this.toastId = null;
      }
    }
  }
}
